import { MenuOptionType } from "@dexteel/mesf-core";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { Box, Button } from "@mui/material";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { useEffect, useMemo, useRef } from "react";

import {
	Product,
	getProductArea,
	PRODUCT_AREA_ASSET_ID,
	PRODUCT_AREA_LABEL,
} from "@/shared/models/Product";
import {
	AREA_COLOR_GALV_WELDED,
	AREA_COLOR_GALV_PUNCHED,
	AREA_COLOR_GALV_SAWED,
	AREA_COLOR_PAINT,
	AREA_COLOR_PUNCH,
	AREA_COLOR_SAWS,
	AREA_COLOR_WAX,
	AREA_COLOR_WELDS,
} from "@/shared/colors";

type Props = {
	setSelectedRowData: (product: Product) => void;
	setShowModal: (modal: "create" | "delete" | "") => void;
	showContextMenu: Function;
	defaultAreaId: number;
	onEdit?: (productId: number, areaAssetId: number) => void;
};

const AREA_PILL_COLOR: Record<
	Exclude<ReturnType<typeof getProductArea>, null>,
	string
> = {
	saw: AREA_COLOR_SAWS,
	weld: AREA_COLOR_WELDS,
	paint: AREA_COLOR_PAINT,
	punch: AREA_COLOR_PUNCH,
	wax: AREA_COLOR_WAX,
	"galv-sawed": AREA_COLOR_GALV_SAWED,
	"galv-punched": AREA_COLOR_GALV_PUNCHED,
	"galv-welded": AREA_COLOR_GALV_WELDED,
};

const AreaPillCell = (params: ICellRendererParams<Product>) => {
	const product = params.data;
	if (!product) return null;
	const area = getProductArea(product);
	if (!area) return null;

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				height: "100%",
			}}
		>
			<Box
				sx={{
					px: 1,
					py: 0.4,
					borderRadius: "3px",
					backgroundColor: AREA_PILL_COLOR[area],
					color: "#fff",
					fontSize: "0.68rem",
					fontWeight: 700,
					letterSpacing: "0.02em",
					textTransform: "uppercase",
					lineHeight: 1.4,
					minWidth: 56,
					textAlign: "center",
				}}
			>
				{PRODUCT_AREA_LABEL[area]}
			</Box>
		</Box>
	);
};

export const useTableData = ({
	setSelectedRowData,
	setShowModal,
	showContextMenu,
	defaultAreaId,
	onEdit,
}: Props) => {
	const showContextMenuRef = useRef(showContextMenu);
	useEffect(() => {
		showContextMenuRef.current = showContextMenu;
	}, [showContextMenu]);

	const onEditRef = useRef(onEdit);
	useEffect(() => {
		onEditRef.current = onEdit;
	}, [onEdit]);

	const actionsColumnDef: ColDef = {
		field: "-",
		headerName: "",
		sortable: false,
		filter: false,
		maxWidth: 50,
		minWidth: 50,
		cellRenderer: (params: ICellRendererParams<Product>) => {
			const product = params.data;
			if (!product) return null;
			return (
				<Box
					sx={{
						height: "100%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Button
						onClick={(e) =>
							showContextMenuRef.current(
								e.nativeEvent,
								product,
								"productsContext",
							)
						}
						title="Options"
						size="small"
						sx={{ minWidth: 28, padding: "4px" }}
					>
						<FormatListBulletedSharpIcon color="action" />
					</Button>
				</Box>
			);
		},
	};

	const columnDefs: ColDef[] = useMemo(
		() => [
			{
				field: "PartNumber",
				headerName: "Part #",
				flex: 1.5,
				minWidth: 120,
				wrapText: true,
				autoHeight: true,
				cellStyle: { textAlign: "left" },
			},
			{
				field: "ExtendedPartNumber",
				headerName: "Extended Part#",
				wrapHeaderText: true,
				flex: 2.3,
				minWidth: 140,
				wrapText: true,
				autoHeight: true,
				cellStyle: { textAlign: "left" },
			},
			{
				field: "GenericName",
				headerName: "Name",
				flex: 1,
				minWidth: 110,
				wrapText: true,
				autoHeight: true,
				cellStyle: { textAlign: "left" },
			},
			{
				field: "CustomerName",
				headerName: "Customer",
				flex: 1.5,
				wrapText: true,
				autoHeight: true,
				minWidth: 120,
			},
			{
				field: "SectionName",
				headerName: "Section",
				flex: 0.9,
				minWidth: 65,
			},
			{
				field: "GradeName",
				headerName: "Grade",
				flex: 0.7,
				minWidth: 55,
			},
			{
				headerName: "Area",
				cellRenderer: AreaPillCell,
				valueGetter: (params) => {
					const product = params.data;
					if (!product) return null;
					const area = getProductArea(product);
					return area ? PRODUCT_AREA_LABEL[area] : null;
				},
				filter: false,
				sortable: true,
				width: 150,
				maxWidth: 150,
				suppressSizeToFit: true,
			},
			actionsColumnDef,
		],
		[],
	);

	const getMenuOptions = (data: Product) => {
		const options: MenuOptionType[] = [];

		if (data) {
			options.push({
				name: "Edit Product",
				key: "edit_product",
				onClick: () => {
					if (data.ProductId) {
						const area = getProductArea(data);
						const areaAssetId = area
							? PRODUCT_AREA_ASSET_ID[area]
							: defaultAreaId;
						onEditRef.current?.(data.ProductId, areaAssetId);
					}
				},
				icon: <EditIcon />,
				style: { fontWeight: "bold" },
			});
		}

		options.push({
			name: "New Product",
			key: "new_product",
			onClick: () => {
				setShowModal("create");
			},
			icon: <PlaylistAddIcon />,
		});

		if (data) {
			options.push({
				name: "Delete Product",
				key: "delete_product",
				onClick: () => {
					setSelectedRowData(data);
					setShowModal("delete");
				},
				icon: <DeleteIcon />,
			});
		}

		return options;
	};

	return { columnDefs, getMenuOptions };
};
